import moment from 'moment';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useInterval } from "../../utils/hooks";
import { useSmsSocket } from "../../utils/smsSocket";
import { connect } from "react-redux";
import SmsMessagesListItem from '../SmsMessagesListItem';
import ContactAvatar from '../ContactAvatar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import InputBase from '@mui/material/InputBase';
import CallIcon from "@mui/icons-material/CallOutlined";
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useStyles } from "./styles";
import EmojiPicker from '../EmojiPicker'
import { setSmsContact, resetSmsContact } from "../../store/features/smsConversations/smsContactSlice";
import { setNewSms, clearSms } from "../../store/features/smsConversations/smsNewMessageSlice";
import { setNewMms, clearMms } from "../../store/features/smsConversations/smsNewMmsSlice";
import { setSmsNewConversationNumber, resetSmsNewConversation } from "../../store/features/smsConversations/smsNewConversationSlice";
import { MuiTelInput } from "mui-tel-input";
import { getSmsContactConversations, sendSms, } from "../../utils/utils";
import { resetSession } from "../../store/features/session/sessionSlice";
import { useDropzone } from "react-dropzone";
import { findConversationContact, conversationDisplayName, contactDisplayName, formatUsPhone } from "../../utils/contacts";

const mapDispatchToProps = {
  setSmsNewConversationNumber,
  resetSmsNewConversation,
  setSmsContact,
  resetSmsContact,
  setNewSms,
  clearSms,
  setNewMms,
  clearMms,
  resetSession,
};

// "Today", "Yesterday", a weekday within the last week, otherwise a full date.
const dayLabel = (dateTime) => {
  const m = moment(dateTime);
  const now = moment();
  if (m.isSame(now, 'day')) return 'Today';
  if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
  if (now.diff(m, 'days') < 7) return m.format('dddd');
  return m.format('LL');
};

// A conversation opens with this many of its most recent messages, and
// "Load earlier messages" adds as many again. The server sends at most
// MAX_MESSAGES at a time.
const PAGE_SIZE = 50;
const MAX_MESSAGES = 500;

const SmsMessagesList = (props) => {
  const {
    history,
    session,
    resetSession,
    smsContact,
    smsNewConversation,
    smsNewMessage,
    smsNewMms,
    user,
    phonebook,
    externalPhonebook,
    personalPhonebook,
    setSmsContact,
    resetSmsContact,
    setSmsNewConversationNumber,
    resetSmsNewConversation,
    setNewSms,
    clearSms,
    setNewMms,
    clearMms,
    setValue,
    handleDialClick,
  } = props;
  const { classes, cx } = useStyles();

  const defaultValues = {
    server: session.lastServer,
    token: session.token,
  };

  const [conversationData, setConversationData] = useState({ number: "", name: "", profileImage: null, messages: [] })
  const [emojiPicker, setEmojiPicker] = useState(false);
  const [telValue, setTelValue] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showJump, setShowJump] = useState(false);

  const listRef = useRef(null);
  const nearBottomRef = useRef(true);
  const prevRef = useRef({ id: null, count: 0 });
  // True from choosing a conversation until its messages have been shown.
  const openAtEndRef = useRef(false);
  // How many of the latest messages to ask for. It grows with "Load earlier
  // messages" and every refresh asks for the same number, so what is on
  // screen stays one unbroken stretch of the conversation.
  const wantedRef = useRef(PAGE_SIZE);
  const wantedForRef = useRef(null);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  // Where the view was before earlier messages were added above it.
  const anchorRef = useRef(null);

  const getMessages = () => {
    // Another conversation starts again from the latest page.
    if (wantedForRef.current !== smsContact.id) {
      wantedForRef.current = smsContact.id;
      wantedRef.current = PAGE_SIZE;
    }
    let fetchMessages = getSmsContactConversations(defaultValues, smsContact, wantedRef.current);

    return Promise.all([fetchMessages])
      .then((res) => {
        for (var i = 0; i < res.length; i++) {
          if (res[i] instanceof Error && res[i].message === "Unauthorized") {
            resetSession();
            history.push('/login');
            return;
          }
        }

        if (res[0].Conversation && res[0].Conversation.length) {
          setConversationData(res[0].Conversation[0])
          // The server says whether earlier messages exist. An older server
          // does not, and sends the whole conversation.
          setHasEarlier(!!(res[0].paging && res[0].paging.hasMore));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const loadEarlier = () => {
    const el = listRef.current;
    if (loadingEarlier || !el) return;
    // Remember the view: the new messages go above it and must not move it.
    anchorRef.current = { height: el.scrollHeight, top: el.scrollTop };
    wantedRef.current = Math.min(MAX_MESSAGES, wantedRef.current + PAGE_SIZE);
    setLoadingEarlier(true);
    getMessages().then(() => setLoadingEarlier(false));
  };

  // Live updates: reload the open thread when an event concerns it (or does
  // not say which conversation). Polling stays as a fallback and slows down
  // while the socket is connected.
  const socketConnected = useSmsSocket(defaultValues, (event, payload) => {
    if (!smsContact.id || smsContact.id === "new") return;
    if (!payload.conversation_id || payload.conversation_id === smsContact.id) {
      getMessages();
    }
  });

  useInterval(() => {
    if (smsContact.id && smsContact.id !== "new") {
      getMessages();
    }
  }, socketConnected ? 300000 : 30000);

  useEffect(() => {
    if (smsContact.id) {
      if (smsContact.id === "new") {
        setConversationData({ number: smsNewConversation.number,
                              name: smsNewConversation.name,
                              profileImage: null,
                              messages: [] });
      }
      else {
        getMessages();
      }
    }
    // eslint-disable-next-line
  }, [smsContact, smsNewConversation.number, smsNewConversation.name])

  const scrollToBottom = (behavior = "smooth") => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }

  // A conversation opens at its most recent message. Choosing one only starts
  // loading it: the messages arrive a moment later, so the jump to the end
  // is owed until they do, wherever the previous conversation was scrolled.
  //
  // After that, new messages are followed only when the user is already at
  // the bottom; otherwise a "new messages" pill is offered, so reading
  // history is not interrupted.
  //
  // In a layout effect, so the thread is never painted at its beginning first.
  useLayoutEffect(() => {
    setValue(conversationData.number)
    const messageCount = (conversationData.messages || []).length;
    const changedConversation = prevRef.current.id !== smsContact.id;
    const anchor = anchorRef.current;
    if (anchor && !changedConversation && messageCount > prevRef.current.count && listRef.current) {
      // Earlier messages were added at the top: keep the same message under
      // the reader's eyes.
      anchorRef.current = null;
      listRef.current.scrollTop = listRef.current.scrollHeight - anchor.height + anchor.top;
    } else if (changedConversation) {
      anchorRef.current = null;
      openAtEndRef.current = true;
      nearBottomRef.current = true;
      scrollToBottom("auto");
      setShowJump(false);
    } else if (openAtEndRef.current) {
      // The messages of the conversation that was just chosen.
      openAtEndRef.current = false;
      nearBottomRef.current = true;
      scrollToBottom("auto");
      setShowJump(false);
    } else if (nearBottomRef.current) {
      // Jump on the first load of a thread; glide only for messages that arrive later.
      scrollToBottom(prevRef.current.count === 0 ? "auto" : "smooth");
      setShowJump(false);
    } else if (messageCount > prevRef.current.count) {
      setShowJump(true);
    }
    prevRef.current = { id: smsContact.id, count: messageCount };
  }, [conversationData, setValue, smsContact.id]);

  // A picture finishes loading after the jump and makes the thread taller.
  // Stay at the end if that is where the user is.
  const handleMediaLoad = () => {
    if (nearBottomRef.current) scrollToBottom("auto");
  };

  useEffect(() => {
    if (smsNewConversation.number === "") {
      setTelValue("");
    }
  }, [smsNewConversation.number]);

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottomRef.current = distance < 80;
    if (nearBottomRef.current) setShowJump(false);
  };

  const onEmojiClickOutside = () => {
    setEmojiPicker(false);
  }

  const handleEmojiPicker = () => {
    setEmojiPicker(!emojiPicker);
  }

  const addEmoji = (e) => {
    setNewSms({ id: smsContact.id, message: (smsNewMessage[smsContact.id] || "") + e.native })
    handleEmojiPicker();
  }

  const messageText = smsNewMessage[smsContact.id] || "";
  const media = smsNewMms[smsContact.id];
  const canSend = messageText.trim() !== "" || !!(media && media.src);

  const sendMessage = (text, mediaSrc) => {
    const fromPhone = user.conversation.number;
    const toPhone = (smsContact.id === "new") ? smsNewConversation.number : conversationData.number;
    if (toPhone === "") {
      alert("You need to specify the number of the receiver.");
      return null;
    }
    const countryCode = (smsContact.id === "new") ? smsNewConversation.countryCode : "";
    const toPhonePretty = (smsContact.id === "new") ? `${countryCode} ${toPhone}` : conversationData.number_pretty;
    return sendSms(defaultValues, fromPhone, toPhone, countryCode, toPhonePretty, text, mediaSrc || "");
  }

  const handleSubmit = e => {
    if (e) e.preventDefault();
    if (!canSend) return;

    const mmsMedia = media ? media.src : "";
    const sendSmsPromise = sendMessage(messageText, mmsMedia);
    if (!sendSmsPromise) return;

    Promise.all([sendSmsPromise])
      .then(() => {
        if (smsContact.id === "new") {
          clearSms("new");
          clearMms("new");
          resetSmsNewConversation();
          resetSmsContact();
        }
        else {
          setSmsContact({ conversation_id: smsContact.id })
          getMessages();
          clearSms(smsContact.id);
          clearMms(smsContact.id);
        }
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to send sms! " + err);
      });
  };

  // Re-send a message that was not delivered.
  const handleRetry = (message) => {
    const sendSmsPromise = sendMessage(message.message || "", message.type === "mms" ? message.file : "");
    if (!sendSmsPromise) return;
    Promise.all([sendSmsPromise])
      .then(() => getMessages())
      .catch((err) => {
        console.error(err);
        alert("Failed to send sms! " + err);
      });
  };

  const handleKeypress = e => {
    // Enter sends; Shift+Enter inserts a new line.
    if (e.key === 'Enter' && e.shiftKey === false) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleClearMedia = () => clearMms(smsContact.id);

  const handlePhoneChange = (value, info) => {
    setTelValue(value);
    const countryCode = info.countryCallingCode ? `+${info.countryCallingCode}` : "+1";
    const inputNumber = info.nationalNumber || "";
    setValue(inputNumber.replace(/\D/g, ""));
    setSmsNewConversationNumber({ number: inputNumber, countryCode: countryCode });
  };

  const renderMessages = () => {
    if (!conversationData)
      return <div></div>

    let i = 0;
    let messageCount = conversationData.messages.length;
    let tempMessages = [];

    while (i < messageCount) {
      let previous = conversationData.messages[i - 1];
      let current = conversationData.messages[i];
      let next = conversationData.messages[i + 1];
      let isMine = current.direction === "sent";
      let currentMoment = moment(current.dateTime);
      let startsSequence = true;
      let endsSequence = true;
      let dateSeparator = null;

      if (!previous || !moment(previous.dateTime).isSame(currentMoment, 'day')) {
        dateSeparator = dayLabel(current.dateTime);
      }

      if (previous && !dateSeparator) {
        let previousMoment = moment(previous.dateTime);
        let previousDuration = moment.duration(currentMoment.diff(previousMoment));
        if (previous.direction === current.direction && previousDuration.as('hours') < 1) {
          startsSequence = false;
        }
      }

      if (next) {
        let nextMoment = moment(next.dateTime);
        let nextDuration = moment.duration(nextMoment.diff(currentMoment));
        if (next.direction === current.direction && nextDuration.as('hours') < 1 && nextMoment.isSame(currentMoment, 'day')) {
          endsSequence = false;
        }
      }

      tempMessages.push(
        <SmsMessagesListItem
          key={i}
          isMine={isMine}
          startsSequence={startsSequence}
          endsSequence={endsSequence}
          dateSeparator={dateSeparator}
          data={current}
          onRetry={isMine ? handleRetry : undefined}
        />
      );

      i += 1;
    }

    return tempMessages;
  }

  const phonebooks = [personalPhonebook, phonebook, externalPhonebook];

  // Phonebook entry for this conversation, if any (personal, internal, external).
  const findContact = () => findConversationContact(conversationData, phonebooks);

  // Conversation name, else the matching contact's name, else nothing.
  const resolvedName = () => conversationDisplayName(conversationData, phonebooks);

  const getName = () => {
    const name = resolvedName();
    return formatUsPhone(name !== ""
      ? name
      : conversationData.number_pretty || conversationData.number);
  }

  const renderStandardHeader = () => {
    const name = getName();
    const subtitle = resolvedName() !== ""
      ? formatUsPhone(conversationData.number_pretty || conversationData.number)
      : null;
    // A name that is itself the number needs no second line.
    const showSubtitle = subtitle && subtitle !== name;
    return <div className={classes.imageNameDivHeader}>
      <ContactAvatar
        name={resolvedName()}
        number={conversationData.number}
        image={conversationData.profileImage}
        size={40}
      />
      <div className={classes.headerText}>
        <div className={classes.headerName}>{name}</div>
        {showSubtitle && <div className={classes.headerSubtitle}>{subtitle}</div>}
      </div>
    </div>
  }

  const renderNewMessageHeader = () => {
    return <div className={classes.newMessagePhoneContainer}>
      <div className={classes.newMessagePhoneLabel}>To:</div>
      <MuiTelInput
        autoFocus
        className={classes.newMessagePhone}
        variant="standard"
        defaultCountry="US"
        onlyCountries={["US"]}
        value={telValue}
        placeholder="Phone number"
        onChange={handlePhoneChange}
        slotProps={{ input: { disableUnderline: true, placeholder: "Phone number" } }}
      />
    </div>
  }

  const renderHeader = () => {
    if (smsContact.id === "")
      return null;

    const isNew = smsContact.id === "new";
    const contact = isNew ? null : findContact();

    return (
      <div className={classes.smsMessagesTop}>
        {isNew ? renderNewMessageHeader() : renderStandardHeader()}
        <div className={classes.headerActions}>
          <Tooltip title="Call">
            <IconButton aria-label="call" className={classes.headerButton} onClick={handleDialClick}>
              <CallIcon />
            </IconButton>
          </Tooltip>
          {!isNew && (
            <>
              <IconButton aria-label="conversation menu" aria-haspopup="true" className={classes.headerButton} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => { setMenuAnchor(null); handleDialClick(); }}>
                  <ListItemIcon><CallIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Call" />
                </MenuItem>
                <MenuItem
                  disabled={!contact}
                  onClick={() => { setMenuAnchor(null); history.push('/dashboard/contacts/all'); }}>
                  <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="View contact" secondary={contact ? contactDisplayName(contact) : "Not in your contacts"} />
                </MenuItem>
              </Menu>
            </>
          )}
        </div>
      </div>
    );
  }

  const smsMessageCounter = (messageLength) => {
    if (messageLength < 161) {
      return 1;
    }
    return Math.ceil(messageLength / 153);
  }

  const renderCharacterCounter = () => {
    const len = messageText.length;
    if (len === 0) return null;
    const segments = smsMessageCounter(len);
    const limit = segments > 1 ? 153 * segments : 160;
    return (
      <p className={cx(classes.smsCharacterCounter, segments > 1 && classes.smsCharacterCounterWarn)}>
        {len} / {limit}{segments > 1 ? ` · ${segments} messages` : ""}
      </p>
    );
  }

  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.map(file => {
      const reader = new FileReader();
      reader.onload = function(e) {
        setNewMms({ id: smsContact.id, mms: {src: e.target.result, use: true }});
      };
      reader.readAsDataURL(file);
      return file;
    });
  }, [setNewMms, smsContact.id]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [], "audio/wav": [], "audio/x-wav": [], "audio/midi": [], "audio/sp-midi": [], "audio/mid": [], "audio/mpeg3": [], "audio/mpeg": [], "audio/aac": [], "audio/qcelp": [], "audio/amr": [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const renderAttachment = () => {
    if (!media || !media.src) return null;
    return (
      <div className={classes.attachmentRow}>
        <div className={classes.attachmentCard}>
          <img src={media.src} alt="attachment" className={classes.attachmentThumb} />
          <IconButton size="small" className={classes.attachmentRemove} aria-label="remove attachment" onClick={handleClearMedia}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.smsMessagesList} {...getRootProps()}>
      <input {...getInputProps()} />
      {isDragActive && <div className={classes.dropOverlay}>Drop media to attach it</div>}
      {renderHeader()}
      <div className={classes.smsMessagesListContainer} ref={listRef} onScroll={handleListScroll} onLoadCapture={handleMediaLoad}>
        {smsContact.id === ""
          ? (
            <div className={classes.noConversation}>
              <div className={classes.noConversationIcon}><ForumOutlinedIcon /></div>
              <div className={classes.noConversationTitle}>No conversation selected</div>
              <div className={classes.noConversationText}>Pick a conversation from the list, or start a new message.</div>
            </div>
          )
          : (
            <>
              {hasEarlier && wantedRef.current < MAX_MESSAGES && smsContact.id !== "new" && (
                <button type="button" className={classes.loadEarlier} onClick={loadEarlier} disabled={loadingEarlier}>
                  {loadingEarlier ? "Loading…" : "Load earlier messages"}
                </button>
              )}
              {renderMessages()}
            </>
          )}
      </div>
      {showJump && (
        <button type="button" className={classes.jumpPill} onClick={() => { scrollToBottom("smooth"); setShowJump(false); }}>
          <ArrowDownwardIcon fontSize="small" /> New messages
        </button>
      )}
      {smsContact.id !== "" && (
        <div className={classes.smsCompose}>
          {renderAttachment()}
          <div className={classes.smsComposeBox}>
            <Tooltip title="Attach media">
              <IconButton className={classes.composeIcon} aria-label="media" onClick={open}>
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Emoji">
              <IconButton className={classes.composeIcon} aria-label="emoji" onClick={() => handleEmojiPicker()}>
                <InsertEmoticonIcon />
              </IconButton>
            </Tooltip>
            <EmojiPicker
              render={emojiPicker}
              onClickOutside={() => onEmojiClickOutside()}
              selectEvent={addEmoji}
            />
            <InputBase
              className={classes.smsComposeInput}
              id="outlined-basic-email"
              multiline
              maxRows={6}
              value={messageText}
              onChange={e => setNewSms({ id: smsContact.id, message: e.target.value })}
              onKeyDown={handleKeypress}
              placeholder="Type a message"
              fullWidth
              slotProps={{ input: {
                maxLength: 918,
                'aria-label': 'message',
              } }} />
            <div className={classes.composeEnd}>
              {renderCharacterCounter()}
              <Tooltip title={canSend ? "Send" : "Type a message or attach media"}>
                <span>
                  <IconButton
                    className={cx(classes.sendButton, canSend && classes.sendButtonEnabled)}
                    aria-label="send"
                    disabled={!canSend}
                    onClick={(e) => handleSubmit(e)}>
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    session: state.session,
    smsContact: state.smsContact,
    smsNewConversation: state.smsNewConversation,
    smsNewMessage: state.smsNewMessage,
    smsNewMms: state.smsNewMms,
    user: state.user,
    phonebook: state.phonebook,
    externalPhonebook: state.externalPhonebook,
    personalPhonebook: state.personalPhonebook,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SmsMessagesList);
